import { BalanceData, Rarity } from "../data/balance";
import { Fix64 } from "../maths/fix64";
import { Pcg32 } from "../maths/pcg32";
import { GearItem, auctionFloor, essenceRefund, generateGear } from "./gear";
import { metaSeed, utcDay } from "./orders";
import { PlayerProfile } from "./profile";
import { Wallet } from "./wallet";

/**
 * The NPC market (ADR 0013, DESIGN §10.2, §10.3; balance/src/market.json; owner decision D7).
 * - Trading Post: sell spare Rare+ gear for Marks at the §9.6 auction floor (× `sellFraction`), which always beats
 *   salvage; buy from a stock rolled from (player, UTC day) at the player's level, for Marks, each item once a day.
 * - Black Market: one featured Legendary or Mythic item a UTC day, rolled from (player, day), priced in gems — the one
 *   place gems buy gear. No countdown, no push.
 * Offers are rolled the same way every time, so what the player is shown is what they buy (an offer carries the day
 * and level it was rolled for; a purchase is refused once either moved on).
 */

/** Pcg32 streams for the stock and the featured item, apart from every other use of the same seeds. */
const STOCK_STREAM = 409;
const BLACK_MARKET_STREAM = 419;
const PERCENT = 100;
/** A stock or featured item's id until it is bought (the profile gives it a real one). */
const OFFER_ITEM_ID = 0;

// ------------------------------------------------------------------ Trading Post: selling

/** Why an item cannot go to the Trading Post, or null when it can. */
export function sellBlocker(balance: BalanceData, item: GearItem): string | null {
  if (item.rarity < balance.commerce.market.minSellRarity) return "below the Trading Post's rarity";
  if (item.equippedTo !== null) return "equipped";
  if (item.locked) return "locked";
  if (item.bound) return "bound";
  return null;
}

/** Marks the Trading Post pays for an item: the §9.6 auction floor (item-level scaled) × `sellFraction`. */
export function sellPrice(balance: BalanceData, item: GearItem): number {
  return Fix64.fromInt(auctionFloor(balance, item)).mul(balance.commerce.market.sellFraction).floorToInt();
}

/** The share of an item's raise spend (Forge Essence) a sale pays back, as salvage does (§9.6, gear v2). */
export function sellRefund(balance: BalanceData, item: GearItem): number {
  return essenceRefund(balance, item);
}

/** Sells an item: it leaves the inventory, Marks and the essence refund come in. Mythic needs `confirmMythic`. */
export function sellToTradingPost(profile: PlayerProfile, wallet: Wallet, itemId: number, now: number, confirmMythic = false): { marks: number; refund: number } | string {
  const item = profile.findGear(itemId);
  if (!item) return "no such item";
  const blocked = sellBlocker(profile.balance, item);
  if (blocked !== null) return blocked;
  if (item.rarity === Rarity.Mythic && !confirmMythic) return "mythic needs confirmation";
  const marks = sellPrice(profile.balance, item), refund = sellRefund(profile.balance, item);
  profile.gear.splice(profile.gear.indexOf(item), 1);
  wallet.grant("marks", marks, "tradeSell", now, `item${item.id}`);
  profile.forge.essence += refund;
  return { marks, refund };
}

// ------------------------------------------------------------------ offers: the stock and the featured item

export interface MarketOffer {
  /** Stock position (0 for the Black Market). */
  readonly index: number;
  readonly day: number;
  readonly level: number;
  /** The item as it will arrive (its id is given on purchase). */
  readonly item: GearItem;
  readonly currency: "marks" | "gems";
  readonly price: number;
}

function rollRarity(weights: readonly number[], rng: Pcg32): Rarity {
  const total = weights.reduce((a, w) => a + w, 0);
  let r = rng.nextInt(total);
  for (let i = 0; i < weights.length; i++) { r -= weights[i]; if (r < 0) return i as Rarity; }
  return Rarity.Common;
}

/** The Trading Post's stock on UTC `day` for this player at `level`, each priced at `buyMarkup` × its sell price. */
export function tradingPostStock(balance: BalanceData, playerId: string, day: number, level: number): MarketOffer[] {
  const m = balance.commerce.market;
  if (m.stockSize <= 0 || m.stockWeights.every((w) => w <= 0)) return [];
  const L = Math.max(1, level), rng = new Pcg32(metaSeed(playerId, "stock", day), STOCK_STREAM);
  return Array.from({ length: m.stockSize }, (_, index) => {
    const item = generateGear(balance, OFFER_ITEM_ID, L, rollRarity(m.stockWeights, rng), rng);
    return { index, day, level: L, item, currency: "marks" as const, price: Fix64.fromInt(sellPrice(balance, item)).mul(m.buyMarkup).floorToInt() };
  });
}

/** The Black Market's featured item on UTC `day` for this player at `level`: Mythic with `mythicPercent`, else Legendary. */
export function blackMarketOffer(balance: BalanceData, playerId: string, day: number, level: number): MarketOffer | null {
  const m = balance.commerce.market;
  const rng = new Pcg32(metaSeed(playerId, "blackMarket", day), BLACK_MARKET_STREAM);
  const rarity = rng.nextInt(PERCENT) < m.blackMarketMythicPercent ? Rarity.Mythic : Rarity.Legendary;
  if (m.blackMarketGems[rarity] <= 0) return null;
  const L = Math.max(1, level);
  return { index: 0, day, level: L, item: generateGear(balance, OFFER_ITEM_ID, L, rarity, rng), currency: "gems", price: m.blackMarketGems[rarity] };
}

// ------------------------------------------------------------------ the day's purchases (the `market` save section)

export interface MarketJson { day: number; bought: number[]; blackMarketDay: number }

export class MarketState {
  /** The UTC day `bought` counts for. */
  day = -1;
  /** Stock positions bought today. */
  bought: number[] = [];
  /** The last UTC day the featured item was bought (−1 never). */
  blackMarketDay = -1;

  private roll(now: number): void {
    const today = utcDay(now);
    if (today > this.day) { this.day = today; this.bought = []; }
  }

  stockBought(now: number, index: number): boolean { this.roll(now); return this.bought.includes(index); }
  blackMarketBought(now: number): boolean { return this.blackMarketDay === utcDay(now); }

  /**
   * Buys a stock item as shown (refused once the day or the player's level moved on), once a day, for Marks.
   * @deprecated Shop pass 2 (docs/SHOP.md §9.2): the Mystery Merchant (meta/shops.ts) sells the Trading Post's gear
   * now; kept (with `tradingPostStock` and the `bought` list) so older saves read and their receipts stay valid.
   */
  buyStock(profile: PlayerProfile, wallet: Wallet, playerId: string, shown: MarketOffer, now: number, level: number): GearItem | string {
    const today = utcDay(now);
    if (shown.currency !== "marks" || shown.day !== today || shown.level !== Math.max(1, level)) return "offer changed";
    const offer = tradingPostStock(profile.balance, playerId, today, level)[shown.index];
    if (!offer) return "no such offer";
    if (this.stockBought(now, offer.index)) return "already bought today";
    const paid = wallet.spend("marks", offer.price, "tradeBuy", now, `stock${offer.index}@${today}`);
    if (paid !== true) return paid;
    this.bought.push(offer.index);
    return profile.receiveGear(offer.item);
  }

  /** Buys the day's featured item as shown, once a day, for gems. */
  buyBlackMarket(profile: PlayerProfile, wallet: Wallet, playerId: string, shown: MarketOffer, now: number, level: number): GearItem | string {
    const today = utcDay(now);
    if (shown.currency !== "gems" || shown.day !== today || shown.level !== Math.max(1, level)) return "offer changed";
    const offer = blackMarketOffer(profile.balance, playerId, today, level);
    if (!offer) return "no offer today";
    if (this.blackMarketBought(now)) return "already bought today";
    const paid = wallet.spend("gems", offer.price, "blackMarket", now, `blackMarket@${today}`);
    if (paid !== true) return paid;
    this.blackMarketDay = today;
    // Bound: resold at once it paid thousands of Marks, so paid gems bought Trading Post gear and Marks skins (market.json:
    // Marks are never sold for money; D7). Salvage still takes it, for gold.
    return profile.receiveGear(offer.item, { bound: true });
  }

  toJson(): MarketJson { return { day: this.day, bought: [...this.bought], blackMarketDay: this.blackMarketDay }; }

  static fromJson(j: unknown): MarketState {
    const s = new MarketState();
    if (typeof j !== "object" || j === null) return s;
    const src = j as Partial<MarketJson>;
    if (Number.isInteger(src.day)) s.day = src.day!;
    if (Array.isArray(src.bought)) s.bought = src.bought.filter((n) => Number.isInteger(n) && n >= 0);
    if (Number.isInteger(src.blackMarketDay)) s.blackMarketDay = src.blackMarketDay!;
    return s;
  }
}
